import { useState, useEffect, useRef } from 'react'
import { useNavigate, useLocation } from 'react-router-dom'
import {
  Box, Grid, Stack, Typography, TextField, Button,
  Select, MenuItem, FormControl, FormHelperText, IconButton
} from '@mui/material'
import { visuallyHidden } from '@mui/utils'
import { useIsMobile } from '@/hooks/Responsive'
import CloseIcon from '@mui/icons-material/Close'
import { useAuth } from '@components/AuthContext';
import { useConfirm } from '@components/ConfirmProvider'
import { userSend } from 'api/users';
import { cmmCd, cmmSend } from 'api/comp';
import { useLang } from '@/i18n/useLang'
import { buildLangHref } from '@data/nav'
import AlertConfirmModal from '@components/Modal/AlertConfirmModal'
import { uploadFileLmsSend } from '@/api/util'
import { getTsid } from '@/api/mypage'
import { getTable } from '@/utils/ChkUtil'

export default function Inquiry() {
  const { isMobile } = useIsMobile()
  const lang = useLang()
  const navigate = useNavigate()
  const location = useLocation()

  const [open, setOpen] = useState(false)
  const { user, loading, isAuthReady } = useAuth();
  const { alert, confirm } = useConfirm();

  const [files, setFiles] = useState([])
  const [pkId, setPkId] = useState('')
  const [attachUuid, setAttachUuid] = useState(null)

  useEffect(() => {
    if (!loading && !isAuthReady) {
      setOpen(true)
    }
  }, [loading, isAuthReady])

  const bulletinTitleRef      = useRef(null);   // 제목 focus
  const bulletinSubstatnceRef = useRef(null);   // 내용 focus
  const [formData, setFormData] = useState({
    bulletinId : '',
    grandDivCode : '', 
    midiumDivCode : '',
    lecture : '',
    email : '',
    bulletinTitle : '',
    bulletinSubstatnce : '',
    accountId : '',
    files : ''
  })

  const [categoryMap, setCategoryMap] = useState([]);
  const [subCategoryMap, setSubCategoryMap] =  useState([]);

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData((prev) => ({ ...prev, [name]: value }))
  }

  const handleCategoryChange = (e) => {
    const value = e.target.value
    setFormData((prev) => ({
      ...prev,
      grandDivCode: value,
      midiumDivCode: '',
    }))
  }
  const handleSubCategoryChange = (e) => {
    const value = e.target.value
    setFormData((prev) => ({
      ...prev,
      midiumDivCode: value,
    }))
  }

  const handleFileChange = (e) => {
    const newFiles = Array.from(e.target.files || [])

    setFiles((prev) => [...prev, ...newFiles])
  }

  const handlePreview = (file) => {
    const fileURL = URL.createObjectURL(file)
    window.open(fileURL, '_blank')
  }

  const handleRemoveFile = (index) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  /* 분류-대분류 호출 */
  const searchCtgy = async () => {

    try {
      const res = await cmmCd('MTMINQTP');
      setCategoryMap(res?.data);
    } catch (error) {
      alert('문의 등록 중 오류가 발생했습니다.1');
    }
  }

  /* 분류-대분류 호출 */
  const searchSubCtgy = async () => {

    try {
      const res = await cmmSend('/com/getSubComCdList', { grpCd : formData.grandDivCode });
      setSubCategoryMap(res?.data);
    } catch (error) {
      alert('문의 등록 중 오류가 발생했습니다.2');
    }
  }
  const initInputData = (e) => {
    setFormData({
      grandDivCode : '', 
      midiumDivCode : '',
      lecture : '',
      email : '',
      bulletinTitle : '',
      bulletinSubstatnce : '',
      accountId : '',
      files : ''
    });
    setFiles([]);
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!formData.grandDivCode) {
      alert("대분류를 선택해 주세요.");
      return;
    }

    if (!formData.midiumDivCode) {
      alert("소분류를 선택해 주세요.");
      return;
    }

    if (!formData.bulletinTitle) {
      alert("필수 제목을 입력해 주세요.");
      bulletinTitleRef.current?.focus();
      return;
    }
    if (!formData.bulletinSubstatnce) {
      alert("필수 내용을 입력해 주세요.");
      bulletinSubstatnceRef.current?.focus();
      return;
    }

    try {
      let submissionData = { ...formData };

      if (files && files.length > 0) {
        const tsidResponse = await getTsid();
        const newPkId = tsidResponse?.data;

        if (newPkId) {
          const fileFormData = new FormData();
          fileFormData.append('files', files[0]);
          fileFormData.append('tableName', getTable('SPC'));
          fileFormData.append('tablePkId', newPkId);

          const uploadResponse = await uploadFileLmsSend(fileFormData);
          const fileUuid = uploadResponse?.data;

          submissionData.bulletinId = newPkId;
          submissionData.attachFileUuid = fileUuid[0];
        } else {
          throw new Error('TSID를 가져오는 데 실패했습니다.');
        }
      }

      // console.log('📩 제출 데이터:', submissionData);
      const res = await userSend('/alarm/saveInf', submissionData);

      if (res.message === 'OK') {
        const ok = await confirm({
          title: '등록완료',
          message: '이용문의 및 제안이 등록되었습니다.',
          confirmText: '확인',
          hideCancel: true,
        })

        if (ok) {
          navigate(buildLangHref('/mypage/inquiry', lang))
        }
      } else {
        alert(res.message || '문의 등록에 실패했습니다.');
      }
    } catch (error) {
      alert('문의 등록 중 오류가 발생했습니다.');
      console.error(error);
    }
  }

  useEffect(() => {
    searchCtgy();
  }, []);

  useEffect(() => {
    searchSubCtgy();
  }, [formData.grandDivCode]);


  useEffect(() => {
    // 아직 로딩 중이면 아무 것도 하지 않음
    if (loading) return;

    if ( user ) {
      setFormData((prev) => ({
        ...prev,
        email : user.email,
        accountId : user.accountId,
      })); 
    // } else {
    //   confirm({
    //     title : '확인',
    //     message : '이용문의 및 제안을 위해서는 로그인이 필요합니다. 로그인페이지로 이동하시겠습니까?',
    //     confirmText : '확인',
    //     cancelText :'취소',
    //     hideCancel : true,
    //   });
    }
  }, [loading, user])
  
  if (!isAuthReady) {
    return (
      <>
        <AlertConfirmModal
          open={open}
          title="안내"
          description="로그인 후 이용 가능합니다."
          confirmText="로그인"
          onClose={() => {
            setOpen(false)
            navigate(buildLangHref('/', lang), { replace: true })
          }}
          onConfirm={() => {
            setOpen(false)
            //navigate(buildLangHref('/login', lang), { replace: true })
            navigate(buildLangHref('/login', lang), {
              state: {
                redirectTo: location.pathname + location.search,
              },
            })
          }}
        />
      </>
    )
  }
  
  return (
    <Box
      component="form"
      onSubmit={handleSubmit}
    >
      {/* 대분류 + 소분류 */}
      <Typography component="label" htmlFor="grandDivCode" fontWeight={500} sx={{ mb: 0.5 }}>
        분류 <Typography component="span" color="error">*</Typography>
      </Typography>
      <Grid container spacing={1} sx={{ mb: 4 }}>
        {/* 대분류 */}
        <Grid item xs={12} md={6}>
          <FormControl size="small" fullWidth>
            <Select
              name="grandDivCode"
              value={formData.grandDivCode}
              onChange={handleCategoryChange}
              displayEmpty
              inputProps={{
                'id': 'grandDivCode',
                'aria-label': '대분류 선택',
              }}
            >
              <MenuItem value="">대분류를 선택해 주세요</MenuItem>
              {categoryMap?.map((opt) => (
                <MenuItem key={opt.comCd} value={opt.comCd}>{opt.comNm}</MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        {/* 소분류 */}
        <Grid item xs={12} md={6}>
          <FormControl size="small" fullWidth disabled={!formData.grandDivCode}>
            <Typography
              component="label"
              htmlFor="midiumDivCode"
              sx={visuallyHidden}
            >
              소분류
            </Typography>
            <Select
              id="midiumDivCode"
              name="midiumDivCode"
              value={formData.midiumDivCode}
              onChange={handleSubCategoryChange}
              displayEmpty
              inputProps={{
                'id': 'midiumDivCode',
                'aria-label': '소분류 선택',
              }}
            >
              <MenuItem value="">소분류를 선택해 주세요</MenuItem>
              {subCategoryMap?.map((opt) => (
                opt.comCd === 'LECINQ' ? null : (
                  <MenuItem key={opt.comCd} value={opt.comCd}>{opt.comNm}</MenuItem>
                )
              ))}
            </Select>
          </FormControl>
        </Grid>
      </Grid>

      {/* 이메일 */}
      <Box sx={{ mb: 4 }}>
        <Typography component="label" htmlFor="inquiryEmail" fontWeight={500} sx={{ mb: 0.5 }}>
          이메일 <Typography component="span" color="error">*</Typography>
          <Box component="span" sx={visuallyHidden}>필수 입력</Box>
        </Typography>
        <Stack direction={isMobile ? 'column' : 'row'} spacing={1} alignItems="center">
          <TextField fullWidth
            id="inquiryEmail"
            value={
                    formData.email? (() => {
                      const [id, domain] = formData.email.split('@');
                      // const shortId = id.length > 7 ? id.slice(0, 7) + '...' : id;
                      return `${id}`;
                    })() : ''
                  }
            disabled={true}
          />
          <Typography sx={{ display: isMobile ? 'none' : 'block' }}>
            @
          </Typography>
          <Typography
            component="label"
            htmlFor="inquiryEmailDomain"
            sx={visuallyHidden}
          >
            이메일 도메인
          </Typography>
          <TextField
            fullWidth
            id="inquiryEmailDomain"
            value={
                    formData.email? (() => {
                      const [id, domain] = formData.email.split('@');
                      // const shortId = id.length > 7 ? id.slice(0, 7) + '...' : id;
                      return `${domain}`;
                    })() : ''
                  }
            // onChange={(e) => setDomain(e.target.value)}
            disabled={true}
            // disabled={domainType !== 'custom'}
          />
          {/* <TextField
            select
            fullWidth
            value={domainType}
            onChange={(e) => handleDomainSelect(e.target.value)}
            placeholder="선택"
            SelectProps={{ displayEmpty: true }}
          >
            <MenuItem value="">
              <Typography color="text.secondary" sx={{ lineHeight: '48px' }}>도메인 선택</Typography>
            </MenuItem>
            {domainOptions.map((opt) => (
              <MenuItem key={opt.value} value={opt.value}>
                {opt.label}
              </MenuItem>
            ))}
          </TextField> */}
        </Stack>
      </Box>

      {/* 제목 */}
      <Box sx={{ mb: 4 }}>
        <Typography component="label" htmlFor="inquiryTitle" fontWeight={500} sx={{ mb: 0.5 }}>
          제목 <Typography component="span" color="error">*</Typography>
          <Box component="span" sx={visuallyHidden}>필수 입력</Box>
        </Typography>
        <TextField
          id="inquiryTitle"
          fullWidth
          placeholder="제목을 입력해 주세요"
          name="bulletinTitle"
          value={formData.bulletinTitle}
          inputRef={bulletinTitleRef}
          onChange={handleChange}
          size="small"
        />
      </Box>

      {/* 내용 */}
      <Box sx={{ mb: 4 }}>
        <Typography component="label" htmlFor="inquiryContent" fontWeight={500} sx={{ mb: 0.5 }}>
          내용 <Typography component="span" color="error">*</Typography>
          <Box component="span" sx={visuallyHidden}>필수 입력</Box>
        </Typography>
        <TextField
          id="inquiryContent"
          multiline
          minRows={6}
          fullWidth
          placeholder="내용을 입력해 주세요"
          name="bulletinSubstatnce"
          value={formData.bulletinSubstatnce}
          inputRef={bulletinSubstatnceRef}
          onChange={handleChange}
        />
      </Box>

      {/* 첨부파일 */}
      <Box sx={{ mb: 4 }}>
        <Typography fontWeight={500} sx={{ mb: 0.5 }}>
          첨부파일
        </Typography>

        {/* 칩 영역 + 파일 선택 버튼 */}
        <Stack 
          direction={isMobile ? 'column' : 'row'} 
          alignItems="stretch"
          sx={{ gap: 1 }}
        >
          
          {/* 파일 칩들 */}
          <Stack
            direction="row"
            sx={{ 
              flexGrow: 1,
              flexWrap: 'wrap',
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'grey.100',
              px: 2,
              py: 1,
              borderRadius: 2,
              minHeight: '48px',
              gap: 1,
            }} 
          >
            {files.map((file, idx) => (
              <Box
                key={idx}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  maxWidth: '100%',
                }}
                onClick={() => handlePreview(file)}
              >
                <Typography
                  sx={{
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    maxWidth: (theme) => isMobile ? `calc(100vw - ${theme.spacing(12)})` : '160px',
                    cursor: 'pointer',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  {file.name}
                </Typography>

                <IconButton
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation()
                    handleRemoveFile(idx)
                  }}
                  sx={{
                    color: 'grey.dark',
                  }}
                >
                  <CloseIcon fontSize="small" />
                </IconButton>
              </Box>
            ))}
          </Stack>

          {/* 파일 선택 버튼 */}
          <Button
            variant="outlined"
            color="primary"
            size="medium"
            component="label"
            sx={{ flexShrink: 0 }}
          >
            파일선택
            <input
              type="file"
              hidden
              multiple
              onChange={handleFileChange}
              accept=".pdf,.jpg,.jpeg,.png,.hwp,.hwpx,.ppt,.pptx,.doc,.docx"
            />
          </Button>
        </Stack>

        <FormHelperText sx={{ mt: 0.5 }}>
          첨부파일은 pdf, jpg, jpeg, png, hwp, hwpx, ppt, pptx, doc, docx 형식만 가능합니다.
        </FormHelperText>
      </Box>


      {/* 안내문 */}
      <Typography 
        variant="body2"
        sx={{
          display: 'flex',
          alignItems: 'center',
          gap: 1, 
          '&::before': {
            content: "''",
            display: 'inline-block',
            width: '4px',
            height: '4px',
            borderRadius: '50%',
            bgcolor: 'grey.600',
          },
        }}
      >
        답변은 입력하신 이메일로 보내드립니다. 혹시 메일이 도착하지 않으면 스팸함을 확인해 주세요.
      </Typography>

      {/* 버튼 */}
      <Stack direction="row" justifyContent="flex-end" spacing={1} sx={{ mt: 5 }}>
        <Button 
          variant="outlined" 
          size="large" 
          color="grey"
          sx={{ width: isMobile ? '100%' : 'auto', mt: isMobile ? 3 : 0 }}
          onClick={initInputData}
        >
          초기화
        </Button>
        <Button 
          type="submit" 
          variant="contained" 
          size="large"
          sx={{ width: isMobile ? '100%' : 'auto', mt: isMobile ? 3 : 0 }}
        >
          등록
        </Button>
      </Stack>
    </Box>
  )
}
